import APIService from "./APIService";

/**
 * SubscriptionsAPI — Frontend service layer for all subscription-related API calls.
 *
 * This service interfaces with the Spring Boot SubscriptionController on the backend.
 * All methods that operate on the "current" subscription resolve the subscription
 * server-side from the authenticated user's session token — no subscriptionId is
 * needed from the client.
 *
 * ┌─────────────────────────────────────────────────────────────────────┐
 * │  SECTION OVERVIEW                                                   │
 * │                                                                     │
 * │  1. Subscription CRUD       — Create, update, get current sub      │
 * │  2. Subscription Lifecycle  — Cancel, undo cancel, reactivate      │
 * │  3. History & Audit         — Past subscriptions, change log       │
 * │  4. Service Plans           — Browse / get plan details            │
 * │  5. Trial & Promotions      — Eligibility check, promo validation  │
 * │  6. Checkout Drafts         — Persist in-progress plan selection   │
 * └─────────────────────────────────────────────────────────────────────┘
 *
 * Expected API response format:
 * {
 *   "success": true/false,
 *   "message": "Descriptive message",
 *   "data": { ... },
 *   "count": number (for list operations)
 * }
 */
const SubscriptionsAPI = {
  // ═══════════════════════════════════════════════════════════════════
  // 1. SUBSCRIPTION CRUD — Create, update, and fetch subscriptions
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Create a new subscription (or start a trial).
   * Used for first-time subscribers and for plan changes during a trial.
   * For paid plan changes on an existing Stripe subscription, use updateSubscription() instead.
   * @param {Object} subscriptionData - The subscription data (planId, isTrial, autoRenew, etc.)
   * @returns {Promise} Promise that resolves with created subscription
   */
  createSubscription: async (subscriptionData) => {
    try {
      console.log(
        "[SubscriptionsAPI] Creating subscription:",
        subscriptionData,
      );
      const response = await APIService.post(
        "/api/v1/app/subscriptions",
        subscriptionData,
      );
      console.log("[SubscriptionsAPI] Create subscription response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error creating subscription:", error);
      throw error;
    }
  },

  /**
   * Update the authenticated user's current subscription (plan change).
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * @param {Object} subscriptionData - The updated subscription data
   * @returns {Promise} Promise that resolves with updated subscription
   */
  updateSubscription: async (subscriptionData) => {
    try {
      console.log(
        "[SubscriptionsAPI] Updating current subscription:",
        subscriptionData,
      );
      const response = await APIService.put(
        `/api/v1/app/subscriptions/user/current`,
        subscriptionData,
      );
      console.log("[SubscriptionsAPI] Update subscription response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error updating subscription:", error);
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════════
  // 2. SUBSCRIPTION LIFECYCLE — Cancel, undo cancel, downgrade mgmt
  //
  //    Cancellation flow:
  //      Active/Trial → cancelSubscription()   → status becomes CANCELLED
  //      CANCELLED    → undoCancelSubscription() → status restored to ACTIVE
  //
  //    During a trial, cancellation keeps access until trialEndDate.
  //    Undo-cancel before trial expires restores the original trial
  //    with the same end date — no new trial consumed, no charge.
  //
  //    After the trial expires, the user must pay full price via
  //    a new subscription (the backend blocks a second trial).
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Cancel the authenticated user's current subscription.
   * - For trials: sets status to CANCELLED, preserves trial end date (access continues).
   * - For paid: schedules cancellation at end of billing period via Stripe.
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * @param {string} reason - Cancellation reason (displayed in audit log)
   * @returns {Promise} Promise that resolves with cancellation confirmation
   */
  cancelSubscription: async (reason = "User requested cancellation") => {
    try {
      console.log(
        "[SubscriptionsAPI] Cancelling current subscription. Reason:",
        reason,
      );
      const response = await APIService.post(
        `/api/v1/app/subscriptions/user/current/cancel`,
        { reason },
      );
      console.log("[SubscriptionsAPI] Cancel subscription response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error cancelling subscription:", error);
      throw error;
    }
  },

  /**
   * Get the current subscription for the authenticated user.
   * Returns the full subscription object including status, trial info,
   * payment cycle, pending downgrades, and plan details.
   * @returns {Promise} Promise that resolves with current subscription
   */
  getCurrentSubscription: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Getting current subscription for authenticated user",
      );

      const response = await APIService.get(
        `/api/v1/app/subscriptions/user/current`,
      );
      console.log(
        "[SubscriptionsAPI] Get current subscription response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error getting current subscription:",
        error,
      );
      throw error;
    }
  },
  // ═══════════════════════════════════════════════════════════════════
  // 3. HISTORY & AUDIT — Past subscriptions and change log
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get subscription history for authenticated user.
   * Returns all past and current subscriptions for the account.
   * @returns {Promise} Promise that resolves with subscription history
   */
  getSubscriptionHistoryByUuid: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Getting subscription history for authenticated user",
      );

      const response = await APIService.get(
        `/api/v1/app/subscriptions/user/history`,
      );
      console.log(
        "[SubscriptionsAPI] Get subscription history response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error getting subscription history:",
        error,
      );
      throw error;
    }
  },

  /**
   * Get subscription change audit log for authenticated user's account.
   * Returns a chronological record of every subscription mutation
   * (plan changes, cancellations, cycle changes, etc.).
   * @returns {Promise} Promise that resolves with audit log entries
   */
  getSubscriptionAuditLog: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Getting subscription audit log for authenticated user",
      );

      const response = await APIService.get(
        `/api/v1/app/subscriptions/user/audit-log`,
      );
      console.log(
        "[SubscriptionsAPI] Get subscription audit log response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error getting subscription audit log:",
        error,
      );
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════════
  // 4. SERVICE PLANS — Browse available plans and get plan details
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get all service plans available for subscription.
   * @returns {Promise} Promise that resolves with service plans list
   */
  getAllServicePlans: async () => {
    try {
      console.log("[SubscriptionsAPI] Getting all service plans");
      const response = await APIService.get("/api/v1/app/service-plans");
      console.log("[SubscriptionsAPI] Service plans response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error getting service plans:", error);
      throw error;
    }
  },

  /**
   * Subscribe to a plan
   * @param {Object} subscriptionData - The subscription data including plan_id and payment_cycle
   * @returns {Promise} Promise that resolves with subscription result
   */
  subscribeToPlan: async (subscriptionData) => {
    try {
      console.log("[SubscriptionsAPI] Subscribing to plan:", subscriptionData);
      const response = await APIService.post(
        "/api/v1/app/subscription/subscribe",
        subscriptionData,
      );
      console.log("[SubscriptionsAPI] Subscribe response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error subscribing to plan:", error);
      throw error;
    }
  },

  /**
   * Get service plan details by ID
   * @param {number} planId - The service plan ID
   * @returns {Promise} Promise that resolves with service plan data
   */
  getServicePlanById: async (planId) => {
    try {
      console.log("[SubscriptionsAPI] Getting service plan by ID:", planId);
      const response = await APIService.get(
        `/api/v1/app/service-plans/${planId}`,
      );
      console.log("[SubscriptionsAPI] Service plan response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error getting service plan by ID:",
        error,
      );
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════════
  // 5. TRIAL & PROMOTIONS — Eligibility checks and promo validation
  //
  //    Trial policy: ONE trial per account, ever. The backend enforces
  //    this with a triple-check (account.hasUsedTrial flag, subscription
  //    history query, and isTrial/convertedFromTrialDate fields).
  //    Even if a user cancels during their trial, the flag persists.
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Check if the user is eligible to start a trial subscription.
   * @returns {Promise} Promise that resolves with trial eligibility info
   * Response includes:
   * - canStartTrial: boolean — whether user can start a new trial
   * - isCurrentlyInTrial: boolean — whether user is currently in a trial
   * - existingTrialEndDate: string|null — trial end date if in trial
   * - reason: string|null — reason code if not eligible:
   *     PAID_SUBSCRIPTION_ACTIVE, TRIAL_ALREADY_USED, TRIAL_ACTIVE
   * - hasUsedTrial: boolean — whether account has ever used a trial
   */
  checkTrialEligibility: async () => {
    try {
      console.log("[SubscriptionsAPI] Checking trial eligibility");
      const response = await APIService.get(
        `/api/v1/app/subscriptions/user/trial-eligibility`,
      );
      console.log("[SubscriptionsAPI] Trial eligibility response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error checking trial eligibility:",
        error,
      );
      throw error;
    }
  },

  /**
   * Validate a promotion/discount code for a specific plan
   * @param {string} code - The promo code entered by the user (e.g., "AARP35")
   * @param {number} planId - The plan ID to validate against
   * @param {string} paymentCycle - "MONTHLY" or "YEARLY"
   * @returns {Promise} Promise that resolves with validation result:
   *   { success: true, valid: true/false, discountPercent, originalPrice, discountedPrice, ... }
   */
  validatePromoCode: async (code, planId, paymentCycle = "MONTHLY") => {
    try {
      console.log(
        "[SubscriptionsAPI] Validating promo code:",
        code,
        "for plan:",
        planId,
      );
      const response = await APIService.post(
        "/api/v1/app/promotions/validate",
        { code, planId, paymentCycle },
      );
      console.log(
        "[SubscriptionsAPI] Promo code validation response:",
        response,
      );
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error validating promo code:", error);
      throw error;
    }
  },

  /**
   * Undo a pending cancellation — restore subscription to ACTIVE status.
   *
   * This is the "Resubscribe" action shown to users with a CANCELLED subscription.
   * - For trials: clears cancelledAt/cancelledReason, restores ACTIVE + autoRenew.
   *   The original trial end date is preserved (user doesn't get extra trial time).
   * - For paid: reverses Stripe's cancel_at_period_end flag so billing continues.
   *
   * NOTE: This only works while the subscription record still exists (status=CANCELLED).
   * Once the trial expires and the TrialExpirationService sets status to EXPIRED,
   * this endpoint will fail — the user must create a new (paid) subscription instead.
   *
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * Backend endpoint: POST /api/v1/app/subscriptions/user/current/undo-cancel
   *
   * @returns {Promise} Promise that resolves with the restored subscription
   */
  undoCancelSubscription: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Undoing cancellation for current subscription",
      );
      const response = await APIService.post(
        `/api/v1/app/subscriptions/user/current/undo-cancel`,
        {},
      );
      console.log("[SubscriptionsAPI] Undo cancel response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error undoing subscription cancellation:",
        error,
      );
      throw error;
    }
  },

  /**
   * Cancel a pending downgrade for the authenticated user's current subscription.
   * When a user schedules a downgrade (e.g., Family → Individual), this reverses
   * that pending change so the current plan continues at the next billing cycle.
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * @returns {Promise} Promise that resolves with updated subscription
   */
  cancelPendingDowngrade: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Cancelling pending downgrade for current subscription",
      );
      const response = await APIService.post(
        `/api/v1/app/subscriptions/user/current/cancel-downgrade`,
        {},
      );
      console.log("[SubscriptionsAPI] Cancel downgrade response:", response);
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error cancelling pending downgrade:",
        error,
      );
      throw error;
    }
  },

  /**
   * Schedule a promo code on the authenticated user's current subscription so it
   * takes effect at the NEXT billing date (the current period is unaffected). The
   * Stripe coupon is attached immediately; the discount appears on the next renewal
   * invoice and onward until the coupon expires.
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * @param {string} code The promo code to schedule
   * @returns {Promise} Promise that resolves with updated subscription
   */
  applyPromoToSubscription: async (code) => {
    try {
      console.log(
        "[SubscriptionsAPI] Scheduling promo code for current subscription:",
        code,
      );
      const response = await APIService.post(
        `/api/v1/app/subscriptions/user/current/apply-promo`,
        { code },
      );
      console.log("[SubscriptionsAPI] Schedule promo response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error scheduling promo code:", error);
      throw error;
    }
  },

  /**
   * Cancel a scheduled (not-yet-effective) promo code on the authenticated user's
   * current subscription. Removes the coupon from Stripe and clears the pending promo.
   * Subscription is resolved server-side from auth token — no subscriptionId needed.
   * @returns {Promise} Promise that resolves with updated subscription
   */
  cancelPendingPromo: async () => {
    try {
      console.log(
        "[SubscriptionsAPI] Cancelling scheduled promo for current subscription",
      );
      const response = await APIService.post(
        `/api/v1/app/subscriptions/user/current/cancel-pending-promo`,
        {},
      );
      console.log(
        "[SubscriptionsAPI] Cancel scheduled promo response:",
        response,
      );
      return response;
    } catch (error) {
      console.error(
        "[SubscriptionsAPI] Error cancelling scheduled promo:",
        error,
      );
      throw error;
    }
  },

  // ═══════════════════════════════════════════════════════════════════
  // 6. CHECKOUT DRAFTS — Persist in-progress plan selection
  //
  //    When a user selects a plan but hasn't completed checkout (e.g.,
  //    they need to add a payment method first), the draft is saved
  //    server-side so it survives page refreshes and section switches.
  // ═══════════════════════════════════════════════════════════════════

  /**
   * Get server-side checkout draft for authenticated user.
   * Returns the previously saved draft (if any) so the UI can restore
   * the selected plan on component mount.
   */
  getCheckoutDraft: async () => {
    try {
      console.log("[SubscriptionsAPI] Getting checkout draft");
      const response = await APIService.get(
        `/api/v1/app/subscriptions/user/checkout-draft`,
      );
      console.log("[SubscriptionsAPI] Checkout draft response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error getting checkout draft:", error);
      throw error;
    }
  },

  /**
   * Save server-side checkout draft for authenticated user.
   * @param {Object} draftData - Selected plan and checkout metadata
   */
  saveCheckoutDraft: async (draftData) => {
    try {
      console.log("[SubscriptionsAPI] Saving checkout draft:", draftData);
      const response = await APIService.put(
        `/api/v1/app/subscriptions/user/checkout-draft`,
        draftData,
      );
      console.log("[SubscriptionsAPI] Save checkout draft response:", response);
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error saving checkout draft:", error);
      throw error;
    }
  },

  /**
   * Clear server-side checkout draft for authenticated user.
   */
  clearCheckoutDraft: async () => {
    try {
      console.log("[SubscriptionsAPI] Clearing checkout draft");
      const response = await APIService.delete(
        `/api/v1/app/subscriptions/user/checkout-draft`,
      );
      console.log(
        "[SubscriptionsAPI] Clear checkout draft response:",
        response,
      );
      return response;
    } catch (error) {
      console.error("[SubscriptionsAPI] Error clearing checkout draft:", error);
      throw error;
    }
  },
};

export default SubscriptionsAPI;
